import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { deleteObject, getBlob, getStorage, ref, uploadBytes } from 'firebase/storage';
import { Subscription, skip } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { currentUserUid } from '../../../utils/firestore-ownership';
import { PdfPasswordError, extractPdfText } from '../../../utils/pdf-text';
import { AlertService } from '../../shared/alert/alert.service';
import { CardItemModel, CardModel, CardStatementModel, CategoryModel } from '../finance.model';
import { FinanceStoreService } from '../finance-store.service';
import { sum } from '../finance-summary.service';
import { BarData, ItemGroup, PIE_OPTIONS, PieData, applyChartTheme, barOptions, byCategoryChart, byDescriptionChart, categoryTotals } from '../finance-charts';
import { ThemeService } from '../../account/settings/services/theme.service';
import { MonthService } from '../../shared/month/month.service';
import { CardDialogComponent } from './card-dialog.component';
import { CardItemDialogComponent, CardItemDialogData } from './card-item-dialog.component';
import { ParsedCardItem, ParsedStatement, parseCardStatement, suggestCategory } from './card-statement-parser';

export interface StatementImportRow extends ParsedCardItem {
  include: boolean,
  category: string,
  // Same description and value already registered in this card and month
  exists: boolean
}

// Statement being read: waiting for a password or showing the items for review
export interface StatementImport {
  statement: CardStatementModel,
  file: Blob,
  password: string,
  needsPassword: boolean,
  incorrectPassword: boolean,
  parsed: ParsedStatement | null,
  rows: StatementImportRow[],
  isReading: boolean,
  isSaving: boolean
}

@Component({
  selector: 'finance-cards',
  standalone: false,
  templateUrl: './cards.component.html',
  styleUrls: ['../finances-list.scss', './cards.component.scss']
})
export class CardsComponent implements OnInit, OnDestroy {
  icons = environment.icons;
  cards: CardModel[] = [];
  selected: CardModel | null = null;
  categories: CategoryModel[] = [];
  items: CardItemModel[] = [];
  statements: CardStatementModel[] = [];
  columns = ['date', 'description', 'category', 'installment', 'value', 'action'];
  reading: StatementImport | null = null;

  categoryChart: PieData | null = null;
  // Same order and colors of the pie
  byCategory: (ItemGroup & { color: string | null })[] = [];
  descriptionChart: BarData | null = null;
  private descriptionGroups: ItemGroup[] = [];
  pieOptions = PIE_OPTIONS;
  barOptions = barOptions(() => this.descriptionGroups);
  isLoading = false;
  isUploading = false;

  months = inject(MonthService);
  private store = inject(FinanceStoreService);
  private dialog = inject(MatDialog);
  private alert = inject(AlertService);
  private themes = inject(ThemeService);
  private subscription?: Subscription;
  private themeSubscription?: Subscription;

  async ngOnInit(): Promise<void> {
    const categories = await this.store.list<CategoryModel>('FinanceCategories');
    this.categories = categories.filter(c => c.group == 'CARD' && c.active).sort((a, b) => a.name.localeCompare(b.name));

    await this.loadCards();
    this.subscription = this.months.month$.subscribe(() => this.loadMonth());
    // Charts are drawn on canvas, so they are rebuilt with the colors of the new theme
    this.themeSubscription = this.themes.dark$().pipe(skip(1)).subscribe(() => this.updateCharts());
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.themeSubscription?.unsubscribe();
  }

  get total(): number {
    return sum(this.items);
  }

  // Totals by category, largest first

  async loadCards(): Promise<void> {
    const cards = await this.store.list<CardModel>('Cards');
    this.cards = cards.sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name));
    this.selected = this.cards.find(c => c.id == this.selected?.id) ?? this.cards.find(c => c.active) ?? this.cards[0] ?? null;
  }

  select(card: CardModel): void {
    if (card.id != this.selected?.id) this.reading = null;
    this.selected = card;
    this.loadMonth();
  }

  async loadMonth(): Promise<void> {
    const card = this.selected;
    const month = this.months.month;
    if (!card) {
      this.items = [];
      this.statements = [];
      this.updateCharts();
      return;
    }

    this.isLoading = true;
    try {
      const [items, statements] = await Promise.all([
        this.store.list<CardItemModel>('CardItems', month),
        this.store.list<CardStatementModel>('CardStatements', month)
      ]);
      // Ignore a response of a card or month that is no longer selected
      if (card != this.selected || month != this.months.month) return;
      this.items = items.filter(i => i.card == card.id).sort((a, b) => b.value - a.value);
      this.statements = statements.filter(s => s.card == card.id);
      this.updateCharts();
    } catch {
      this.alert.show('Load failed!', 'error');
    } finally {
      this.isLoading = false;
    }
  }

  private updateCharts(): void {
    applyChartTheme();
    this.byCategory = categoryTotals(this.items);
    this.categoryChart = byCategoryChart(this.items);
    const byDescription = byDescriptionChart(this.items);
    this.descriptionGroups = byDescription?.groups ?? [];
    this.descriptionChart = byDescription?.data ?? null;
  }

  //#region cards

  editCard(card?: CardModel): void {
    this.dialog.open(CardDialogComponent, { data: { ...card } }).afterClosed().subscribe(async (result?: CardModel) => {
      if (!result) return;
      try {
        if (result.id) {
          await this.store.update('Cards', result);
        } else {
          result.id = await this.store.add('Cards', result);
          this.selected = result;
        }
        this.alert.show('Saved!', 'save_as');
        await this.loadCards();
        await this.loadMonth();
      } catch {
        this.alert.show('Save failed!', 'error');
      }
    });
  }

  // Cards with items are kept: mark them as inactive instead
  async deleteCard(card: CardModel): Promise<void> {
    try {
      const [items, statements] = await Promise.all([
        this.store.list<CardItemModel>('CardItems'),
        this.store.list<CardStatementModel>('CardStatements')
      ]);
      if (items.some(i => i.card == card.id) || statements.some(s => s.card == card.id)) {
        this.alert.show('Card has items or statements, set it as inactive instead.', 'error');
        return;
      }

      await this.store.remove('Cards', card.id);
      this.alert.show('Deleted!', 'delete_forever');
      await this.loadCards();
      await this.loadMonth();
    } catch {
      this.alert.show('Delete failed!', 'error');
    }
  }

  //#endregion

  //#region items

  editItem(item?: CardItemModel): void {
    const data: CardItemDialogData = { item: { ...item }, categories: this.categories };
    this.dialog.open(CardItemDialogComponent, { data }).afterClosed().subscribe(async (result?: CardItemModel) => {
      if (!result || !this.selected) return;
      try {
        if (result.id) await this.store.update('CardItems', result);
        else await this.store.add('CardItems', { ...result, card: this.selected.id, month: this.months.month });
        this.alert.show('Saved!', 'save_as');
        await this.loadMonth();
      } catch {
        this.alert.show('Save failed!', 'error');
      }
    });
  }

  async deleteItem(item: CardItemModel): Promise<void> {
    try {
      await this.store.remove('CardItems', item.id);
      this.alert.show('Deleted!', 'delete_forever');
      await this.loadMonth();
    } catch {
      this.alert.show('Delete failed!', 'error');
    }
  }

  //#endregion

  //#region statements

  async upload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.item(0);
    input.value = '';
    if (!file || !this.selected) return;

    if (file.type !== 'application/pdf' || file.size > 10 * 1024 * 1024) {
      this.alert.show('Select a PDF up to 10 MB.', 'error');
      return;
    }

    this.isUploading = true;
    try {
      const month = this.months.month;
      const fileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storageRef = ref(getStorage(), `CardStatements/${currentUserUid()}/${this.selected.id}/${month}/${crypto.randomUUID()}-${fileName}`);
      await uploadBytes(storageRef, file, { contentType: 'application/pdf' });
      const statement = { month, card: this.selected.id, name: file.name, storagePath: storageRef.fullPath };
      const id = await this.store.add('CardStatements', statement);
      this.alert.show('File uploaded!', 'attach_file_add');
      await this.loadMonth();
      await this.startReading({ ...statement, id }, file);
    } catch {
      this.alert.show('Upload failed!', 'fmd_bad');
    } finally {
      this.isUploading = false;
    }
  }

  async download(statement: CardStatementModel): Promise<void> {
    if (!statement.storagePath) {
      this.alert.show('File reference not found, upload it again.', 'fmd_bad');
      return;
    }
    try {
      const blob = await getBlob(ref(getStorage(), statement.storagePath));
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = statement.name;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      this.alert.show('Download failed!', 'fmd_bad');
    }
  }

  async deleteStatement(statement: CardStatementModel): Promise<void> {
    try {
      // Statements uploaded before the storagePath fix have no file reference
      if (statement.storagePath) await deleteObject(ref(getStorage(), statement.storagePath));
    } catch (error: any) {
      // The record is removed even if the file is already gone
      if (error?.code != 'storage/object-not-found') {
        console.error('CardsComponent -> deleteStatement (storage)', statement.storagePath, error);
        this.alert.show(`Delete failed (file): ${error?.code ?? 'unknown'}`, 'error');
        return;
      }
    }

    try {
      await this.store.remove('CardStatements', statement.id);
    } catch (error: any) {
      console.error('CardsComponent -> deleteStatement (firestore)', statement.id, error);
      this.alert.show(`Delete failed (record): ${error?.code ?? 'unknown'}`, 'error');
      return;
    }

    if (this.reading?.statement.id == statement.id) this.reading = null;
    this.alert.show('Deleted!', 'delete_forever');
    await this.loadMonth();
  }

  //#endregion

  //#region statement reading

  // Reads a statement already uploaded
  async read(statement: CardStatementModel): Promise<void> {
    if (!statement.storagePath) {
      this.alert.show('File reference not found, upload it again.', 'fmd_bad');
      return;
    }
    try {
      await this.startReading(statement, await getBlob(ref(getStorage(), statement.storagePath)));
    } catch (error) {
      console.error('CardsComponent -> read', error);
      this.alert.show('Download failed!', 'fmd_bad');
    }
  }

  async startReading(statement: CardStatementModel, file: Blob): Promise<void> {
    this.reading = {
      statement, file, password: '', needsPassword: false, incorrectPassword: false,
      parsed: null, rows: [], isReading: false, isSaving: false
    };
    await this.readText();
  }

  async readText(): Promise<void> {
    const reading = this.reading;
    if (!reading) return;

    reading.isReading = true;
    try {
      const text = await extractPdfText(reading.file, reading.password || undefined);
      reading.needsPassword = false;
      reading.parsed = parseCardStatement(text);
      reading.rows = await this.buildRows(reading.parsed);
      // The panel explains it; the text helps to support a new statement layout
      if (!reading.rows.length)
        console.info('CardsComponent -> readText, no items found in:', text);
    } catch (error) {
      if (error instanceof PdfPasswordError) {
        reading.needsPassword = true;
        reading.incorrectPassword = error.incorrect;
      } else {
        console.error('CardsComponent -> readText', error);
        this.alert.show('Could not read the PDF.', 'fmd_bad');
        this.reading = null;
      }
    } finally {
      reading.isReading = false;
    }
  }

  private async buildRows(parsed: ParsedStatement): Promise<StatementImportRow[]> {
    // Latest months first, so the most recent category wins
    const history = (await this.store.list<CardItemModel>('CardItems')).sort((a, b) => b.month.localeCompare(a.month));
    const categories = this.categories.map(c => c.name);
    const existing = this.items.map(i => `${i.description.toUpperCase()}|${Number(i.value).toFixed(2)}`);

    return parsed.items.map(item => {
      const exists = existing.includes(`${item.description.toUpperCase()}|${item.value.toFixed(2)}`);
      return {
        ...item,
        category: suggestCategory(item, history, categories),
        exists,
        // Payments of the previous statement are not expenses
        include: item.kind != 'payment' && !exists
      };
    });
  }

  get readingTotal(): number {
    return sum((this.reading?.rows ?? []).filter(r => r.include));
  }

  get hasSelectedRows(): boolean {
    return !!this.reading?.rows.some(r => r.include);
  }

  toggleAll(include: boolean): void {
    this.reading?.rows.forEach(r => r.include = include);
  }

  async generateItems(): Promise<void> {
    const reading = this.reading;
    if (!reading) return;

    const rows = reading.rows.filter(r => r.include && r.description.trim());
    reading.isSaving = true;
    try {
      for (const row of rows)
        await this.store.add('CardItems', {
          // Card and month of the statement, even if another one is selected now
          card: reading.statement.card,
          month: reading.statement.month,
          description: row.description.trim(),
          category: row.category,
          value: Number(row.value),
          installment: row.installment,
          installments: row.installments,
          date: row.date,
          statement: reading.statement.id
        });
      this.alert.show(`${rows.length} items created!`, 'save_as');
      this.reading = null;
    } catch (error) {
      console.error('CardsComponent -> generateItems', error);
      this.alert.show('Create failed! Check the items already created before trying again.', 'error');
    } finally {
      reading.isSaving = false;
      await this.loadMonth();
    }
  }

  //#endregion
}
